import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    getOperateType,
    getPointRect,
    IExportLayerOriginInfo,
    MoveListenerLevel,
    useFramework,
    useIdentity,
    useMoveEventListener,
} from '@/framework';
import { isHotKeyPress, isLeftButton } from '@/utils/event-kit';
import { IExportPlugin, ILayerProps, PluginType } from '@/version-types';
import groupInnerState, { getGroupInnerState, useGroupInnerState } from './states/group-inner-state';

const groupInnerOperateType = 'group-inner';

// 选中图层信息
const GroupInnerPick = React.memo(() => {
    const identity = useIdentity();
    const framework = useFramework();
    const pickIds = Array.from(useGroupInnerState()((state) => state.pickIds));
    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(framework.service.frame.getActiveLayer());
    const activeIdRef = useRef<string | undefined>(activeLayer?.id);
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<Record<string, IExportLayerOriginInfo>>();
    const isMove = useRef(false);
    const [_, setRefresh] = useState(0);
    const moveOffset = useRef({ x: 0, y: 0 });

    const reset = useCallback((clear = true) => {
        const ids = getGroupInnerState(identity).getPickIds();
        if (oldInfo.current) {
            if (originLayerInfo.current) {
                for (const id of ids) {
                    const res = originLayerInfo.current[id];
                    if (res) {
                        const { childOriginInfoMap, originInfo } = res;
                        framework.service.frame.updateLayerPosition(
                            id,
                            moveOffset.current.x,
                            moveOffset.current.y,
                            originInfo!,
                            childOriginInfoMap,
                            {
                                isStart: false,
                                isEnd: true,
                            },
                        );
                    }
                }
            }
            const layerId = oldInfo.current.id;
            const activeId = framework.service.frame.getActivePickLayer()?.id;
            if (activeId && activeId === layerId) {
                const layer = framework.service.flatLayer.getLayer(layerId, true);
                framework.service.frame.addOrUpdateLayer(layer);
                if (oldInfo.current) {
                    framework.service.history.write(oldInfo.current);
                }
                framework.service.frame.setActiveLayer(layerId);
            }
        }
        isMove.current = false;
        oldInfo.current = undefined;
        originLayerInfo.current = undefined;
        if (ids.length) {
            setRefresh((v) => v + 1);
        }
        if (clear) {
            getGroupInnerState(identity).clear();
        }
    }, []);

    // 实现组内鼠标点击
    useEffect(() => {
        const mouseDownPoint = { x: 0, y: 0 };
        const downHandle = framework.service.editor.onMouseDown((e) => {
            if (!isLeftButton(e)) return;
            if (framework.service.formatBrush.getState()) return;

            mouseDownPoint.x = e.pageX;
            mouseDownPoint.y = e.pageY;
            const hasHotKey = isHotKeyPress();
            if (!hasHotKey) {
                const operateType = getOperateType(e.target);
                if (operateType !== groupInnerOperateType) {
                    // console.log('------------------reset');
                    reset();
                }
            }
        }, 2);

        const upHandle = framework.service.editor.onMouseUp((e) => {
            if (!isLeftButton(e)) return;
            if (framework.service.formatBrush.getState()) return;

            const mouseUpPoint = { x: e.pageX, y: e.pageY };
            const isMove = mouseUpPoint.x !== mouseDownPoint.x || mouseUpPoint.y !== mouseDownPoint.y;
            if (isMove) return;

            const activeLayer = framework.service.frame.getActiveLayer();
            if (!activeLayer || !framework.service.flatLayer.checkIsFormalGroup(activeLayer) || activeLayer.lock)
                return;

            const mouseDownRect = getPointRect(framework.service.transform.getScenePoint(e));
            const groupInnerId = framework.service.transform.tryPickChild(mouseDownRect, activeLayer.children);

            const hasHotKey = isHotKeyPress();
            if (!hasHotKey) {
                const operateType = getOperateType(e.target);
                if (operateType === groupInnerOperateType) {
                    reset();
                    if (groupInnerId) {
                        framework.service.frame.setActiveLayer(groupInnerId);
                    }
                }
                return operateType === groupInnerOperateType;
            }

            if (groupInnerId) {
                const ids: Set<string> = new Set();
                ids.add(groupInnerId);
                const activeInfo = framework.service.frame.getActiveInfo();
                if (activeInfo?.info.pickId) {
                    ids.add(activeInfo.info.pickId);
                    framework.service.frame.setActiveLayer(activeInfo.info.id);
                }
                getGroupInnerState(identity).setPickIds(Array.from(ids));
            }

            return !!groupInnerId;
        }, 2);
        return () => {
            framework.service.events.off(downHandle);
            framework.service.events.off(upHandle);
        };
    }, []);

    useEffect(() => {
        const handle = framework.service.frame.onActiveLayerDataUpdate((layer) => {
            setActiveLayer(layer);
            activeIdRef.current = layer?.id;
            if (
                layer?.id !== activeIdRef.current ||
                (layer && (!framework.service.flatLayer.checkIsFormalGroup(layer) || layer.lock))
            ) {
                reset();
            }
        });
        return () => {
            framework.service.events.off(handle);
        };
    }, [reset]);

    const isNoRender =
        !pickIds.length ||
        !activeIdRef.current ||
        !activeLayer ||
        !framework.service.flatLayer.checkIsFormalGroup(activeLayer);

    useEffect(() => {
        getGroupInnerState(identity).setIsActive(!isNoRender);
    }, [isNoRender]);

    useMoveEventListener(
        (e) => {
            if (isNoRender) return;
            const ids = getGroupInnerState(identity).getPickIds();
            const operateType = getOperateType(e.target);
            if (e.isStart && ids.length && operateType === groupInnerOperateType) {
                isMove.current = false;
                oldInfo.current = framework.service.flatLayer.getLayer(activeLayer.id, true);
                const infoMap: Record<string, IExportLayerOriginInfo> = {};
                ids.forEach((id) => {
                    infoMap[id] = framework.service.frame.exportLayerOriginInfo(id);
                });
                originLayerInfo.current = infoMap;
            }
        },
        MoveListenerLevel.PREPARE_LEVEL,
        'move-pre',
    );

    useMoveEventListener(
        (e) => {
            if (isNoRender) return;
            const operateType = getOperateType(e.target);
            if (operateType !== groupInnerOperateType) return;
            if (!oldInfo.current || !originLayerInfo.current) return;
            const ids = getGroupInnerState(identity).getPickIds();
            if (!ids.length) return;
            isMove.current = true;

            const scale = framework.service.edgelessCanvas.zoom;
            const x = e.displacementX / scale;
            const y = e.displacementY / scale;

            moveOffset.current.x = x;
            moveOffset.current.y = y;

            for (const id of ids) {
                const res = originLayerInfo.current[id];
                if (res) {
                    const { childOriginInfoMap, originInfo } = res;
                    framework.service.frame.updateLayerPosition(id, x, y, originInfo!, childOriginInfoMap, {
                        isStart: e.isStart,
                        isEnd: e.isEnd,
                    });
                    if (!e.isEnd) {
                        setRefresh((v) => v + 1);
                    }
                }
            }

            if (e.isEnd) {
                reset(false);
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'move-default',
    );

    if (isNoRender) return null;

    return (
        <>
            {isMove.current ? null : (
                <BaseStagePlugin name="group-pick-wrapper">
                    <BaseOperateView layer={framework.service.flatLayer.getLayer(activeLayer.id)}>
                        <div className={classNames('group-pick-layer group-pick-layer-top')}></div>
                        <div className={classNames('group-pick-layer group-pick-layer-left')}></div>
                        <div className={classNames('group-pick-layer group-pick-layer-right')}></div>
                        <div className={classNames('group-pick-layer group-pick-layer-bottom')}></div>
                    </BaseOperateView>
                </BaseStagePlugin>
            )}
            <BaseStagePlugin name="group-pick-layer">
                {pickIds.map((id) => {
                    const layer = framework.service.flatLayer.getLayer(id);
                    return (
                        <BaseOperateView key={id} layer={layer}>
                            <div
                                data-operate-type={groupInnerOperateType}
                                className={classNames('group-pick-item', {
                                    'is-move': isMove.current,
                                })}
                            ></div>
                        </BaseOperateView>
                    );
                })}
            </BaseStagePlugin>
        </>
    );
});

const groupInnerPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 1,
    name: 'group-pick-layer',
    render() {
        return <GroupInnerPick />;
    },
    createState() {
        return [groupInnerState];
    },
};

export default groupInnerPlugin;
